/**
 * Resolve the Affable / tenant organization name the user is working in.
 * Profile shows this as read-only context (not editable personal profile data).
 */

import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { getCurrentUser } from "@utils/userUtils";

export const WORKING_ORGANIZATION_NAME_KEY = "working_organization_name";

type JwtOrgClaims = {
  organization_name?: unknown;
  organization_display_name?: unknown;
  org_name?: unknown;
  organization?: { display_name?: unknown; name?: unknown } | unknown;
  org?: { display_name?: unknown; name?: unknown } | unknown;
};

function asNonEmptyString(value: unknown): string {
  if (value == null) return "";
  const s = String(value).trim();
  return s;
}

/** Decode JWT payload claims for display only (no signature verification). */
export function readAccessTokenOrgClaims(
  accessToken: string | null | undefined,
): string {
  const token = String(accessToken ?? "").trim();
  if (!token || token.split(".").length < 2) return "";
  try {
    const payloadPart = token.split(".")[1];
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const pad = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
    const json = atob(normalized + pad);
    const claims = JSON.parse(json) as JwtOrgClaims;
    const direct =
      asNonEmptyString(claims.organization_name) ||
      asNonEmptyString(claims.organization_display_name) ||
      asNonEmptyString(claims.org_name);
    if (direct) return direct;
    const organization =
      claims.organization && typeof claims.organization === "object"
        ? (claims.organization as { display_name?: unknown; name?: unknown })
        : null;
    const org =
      claims.org && typeof claims.org === "object"
        ? (claims.org as { display_name?: unknown; name?: unknown })
        : null;
    return (
      asNonEmptyString(organization?.display_name) ||
      asNonEmptyString(organization?.name) ||
      asNonEmptyString(org?.display_name) ||
      asNonEmptyString(org?.name)
    );
  } catch {
    return "";
  }
}

export function persistWorkingOrganizationName(name: string | null | undefined): void {
  if (typeof window === "undefined") return;
  const trimmed = asNonEmptyString(name);
  try {
    if (trimmed) {
      localStorage.setItem(WORKING_ORGANIZATION_NAME_KEY, trimmed);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export function readPersistedWorkingOrganizationName(): string {
  if (typeof window === "undefined") return "";
  try {
    return asNonEmptyString(localStorage.getItem(WORKING_ORGANIZATION_NAME_KEY));
  } catch {
    return "";
  }
}

/**
 * Best-effort organization name for the current Hub session.
 * Prefer explicit login selection / JWT tenant claims over facility legal names.
 */
export function resolveWorkingOrganizationName(): string {
  if (typeof window === "undefined") return "";

  const persisted = readPersistedWorkingOrganizationName();
  if (persisted) return persisted;

  const user = getCurrentUser() as Record<string, unknown> | null;
  if (user) {
    const fromUser =
      asNonEmptyString(user.organization_name) ||
      asNonEmptyString(user.org_name) ||
      asNonEmptyString(user.organization_display_name) ||
      asNonEmptyString(user.organizationName);
    if (fromUser) return fromUser;
  }

  try {
    const fromToken = readAccessTokenOrgClaims(localStorage.getItem("access_token"));
    if (fromToken) return fromToken;
  } catch {
    /* ignore */
  }

  const selected = getSelectedFacilityFromSession<{
    organization_name?: string | null;
    org_name?: string | null;
  }>();
  const fromSelected =
    asNonEmptyString(selected?.organization_name) ||
    asNonEmptyString(selected?.org_name);
  if (fromSelected) return fromSelected;

  const facilities = Array.isArray(user?.facilities)
    ? (user!.facilities as Array<Record<string, unknown>>)
    : [];
  for (const facility of facilities) {
    const name =
      asNonEmptyString(facility.organization_name) ||
      asNonEmptyString(facility.org_name);
    if (name) return name;
  }

  return "";
}
